/**
 * Admin Reservations - Multi-club reservation management
 * Supervises reservations from all clubs:
 * - Playtomic reservations (synced via API)
 * - Local reservations (made directly at club)
 * - API configuration per club
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, Clock, User, DollarSign, ChevronLeft, RefreshCw,
  CheckCircle, XCircle, AlertCircle, CreditCard, Building2,
  ChevronRight, ChevronDown, ChevronUp, Search, Key, 
  Phone, Users, MapPin, Loader2, X, Wifi, WifiOff, Settings,
  ExternalLink, Plus, Eye, EyeOff, Save, Trash2, Edit, Globe, Home
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { adminAPI, playtomicAPI } from '../../services/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-500', label: 'Pendiente' },
    confirmed: { bg: 'bg-blue-500/20', text: 'text-blue-500', label: 'Confirmado' },
    paid: { bg: 'bg-green-500/20', text: 'text-green-500', label: 'Pagado' },
    cancelled: { bg: 'bg-red-500/20', text: 'text-red-500', label: 'Cancelado' },
    no_show: { bg: 'bg-gray-500/20', text: 'text-gray-500', label: 'No Show' },
  };
  
  const { bg, text, label } = config[status] || config.pending;
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${bg} ${text}`}>
      {label}
    </span>
  );
};

// Source badge (Playtomic vs Local)
const SourceBadge = ({ source }) => {
  if (source === 'playtomic') {
    return (
      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-purple-500/20 text-purple-400">
        <Globe className="w-3 h-3" />
        Playtomic
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-500/20 text-blue-400">
      <Home className="w-3 h-3" />
      Local
    </span>
  );
};

// Reservation card component
const ReservationCard = ({ reservation, showClub = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-card rounded-xl border border-border p-4 hover:border-primary/50 transition-colors"
  >
    <div className="flex items-start justify-between mb-3">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <SourceBadge source={reservation.source} />
          <StatusBadge status={reservation.status} />
        </div>
        <h3 className="font-semibold">{reservation.player_name}</h3>
        {showClub && (
          <p className="text-xs text-muted-foreground">{reservation.club_name}</p>
        )}
      </div>
      <div className="text-right">
        <p className="font-bold text-primary">${reservation.price?.toFixed(2)}</p>
        <p className="text-xs text-muted-foreground">MXN</p>
      </div>
    </div>
    
    <div className="grid grid-cols-2 gap-2 text-sm">
      <div className="flex items-center gap-2 text-muted-foreground">
        <MapPin className="w-4 h-4" />
        {reservation.court_name}
      </div>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Clock className="w-4 h-4" />
        {reservation.start_time} - {reservation.end_time}
      </div>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Users className="w-4 h-4" />
        {reservation.num_players} jugadores
      </div>
      {reservation.player_phone && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Phone className="w-4 h-4" />
          {reservation.player_phone}
        </div>
      )}
    </div>
  </motion.div>
);

// Club card for reservations list
const ClubReservationsCard = ({ club, reservations, onViewDetails, onConfigureApi }) => {
  const [expanded, setExpanded] = useState(false);
  
  const playtomicCount = reservations.filter(r => r.source === 'playtomic').length;
  const localCount = reservations.filter(r => r.source === 'local').length;
  const totalRevenue = reservations.reduce((sum, r) => sum + (r.price || 0), 0);
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-xl border border-border overflow-hidden"
    >
      {/* Club Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-secondary/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-primary/20">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold">{club.name}</h3>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {club.city}, {club.country}
              </p>
              <div className="flex items-center gap-2 mt-1">
                {club.playtomic_enabled ? (
                  <span className="flex items-center gap-1 text-xs text-green-400">
                    <Wifi className="w-3 h-3" />
                    API Activa
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-gray-400">
                    <WifiOff className="w-3 h-3" />
                    Sin API
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-lg font-bold">{reservations.length}</p>
              <p className="text-xs text-muted-foreground">reservaciones</p>
            </div>
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            )}
          </div>
        </div>
        
        {/* Quick stats */}
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border text-sm">
          <div className="flex items-center gap-1">
            <Globe className="w-4 h-4 text-purple-400" />
            <span>{playtomicCount} Playtomic</span>
          </div>
          <div className="flex items-center gap-1">
            <Home className="w-4 h-4 text-blue-400" />
            <span>{localCount} Locales</span>
          </div>
          <div className="flex items-center gap-1 ml-auto">
            <DollarSign className="w-4 h-4 text-green-400" />
            <span className="font-semibold">${totalRevenue.toLocaleString()}</span>
          </div>
        </div>
      </div>
      
      {/* Expanded Content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-border p-4 space-y-3"
          >
            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={(e) => { e.stopPropagation(); onViewDetails(club); }}
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Ver todas
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={(e) => { e.stopPropagation(); onConfigureApi(club); }}
              >
                <Key className="w-4 h-4 mr-1" />
                Configurar API
              </Button>
            </div>
            
            {/* Recent reservations preview */}
            {reservations.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Últimas reservaciones:</p>
                {reservations.slice(0, 3).map((res) => (
                  <div key={res.id} className="flex items-center justify-between p-2 bg-secondary/50 rounded-lg text-sm">
                    <div className="flex items-center gap-2">
                      <SourceBadge source={res.source} />
                      <span className="font-medium">{res.player_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">{res.start_time}</span>
                      <StatusBadge status={res.status} />
                    </div>
                  </div>
                ))}
                {reservations.length > 3 && (
                  <p className="text-xs text-muted-foreground text-center">
                    + {reservations.length - 3} más reservaciones
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No hay reservaciones para hoy
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// API Configuration Modal
const ApiConfigModal = ({ club, onClose, onSave }) => {
  const [apiKey, setApiKey] = useState(club?.playtomic_api_key || '');
  const [apiSecret, setApiSecret] = useState(club?.playtomic_api_secret || '');
  const [clubId, setClubId] = useState(club?.playtomic_club_id || '');
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const handleTest = async () => {
    if (!apiKey || !clubId) {
      toast.error('Ingresa al menos el API Key y Club ID para probar');
      return;
    }
    setTesting(true);
    try {
      // Mock test - in real implementation would call backend
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success('✅ Conexión exitosa con Playtomic');
    } catch (err) {
      toast.error('❌ Error de conexión. Verifica las credenciales.');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        playtomic_api_key: apiKey || null,
        playtomic_api_secret: apiSecret || null,
        playtomic_club_id: clubId || null,
        playtomic_enabled: !!(apiKey && clubId)
      });
      toast.success('Configuración guardada');
      onClose();
    } catch (err) {
      toast.error('Error guardando configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('¿Desconectar la API de Playtomic para este club?')) return;
    setSaving(true);
    try {
      await onSave({
        playtomic_api_key: null,
        playtomic_api_secret: null,
        playtomic_club_id: null,
        playtomic_enabled: false
      });
      toast.success('API desconectada');
      onClose();
    } catch (err) {
      toast.error('Error desconectando');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="w-5 h-5 text-primary" />
            Configurar API Playtomic
          </DialogTitle>
          <DialogDescription>
            Conecta la cuenta de Playtomic de {club?.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current Status */}
          <div className={`p-3 rounded-lg ${club?.playtomic_enabled ? 'bg-green-500/10 border border-green-500/30' : 'bg-secondary'}`}>
            <div className="flex items-center gap-2">
              {club?.playtomic_enabled ? (
                <>
                  <Wifi className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 font-medium">API Conectada</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">API No configurada</span>
                </>
              )}
            </div>
            {club?.last_playtomic_sync && (
              <p className="text-xs text-muted-foreground mt-1">
                Última sincronización: {new Date(club.last_playtomic_sync).toLocaleString('es-MX')}
              </p>
            )}
          </div>

          {/* Info box */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-sm">
            <p className="text-blue-400 font-medium mb-1">¿Dónde encuentro mis credenciales?</p>
            <ol className="text-muted-foreground space-y-1 list-decimal list-inside text-xs">
              <li>Inicia sesión en tu cuenta de Playtomic Business</li>
              <li>Ve a Configuración → API & Integraciones</li>
              <li>Genera o copia tus credenciales de API</li>
              <li>El Club ID aparece en la URL de tu club</li>
            </ol>
          </div>

          {/* API Key */}
          <div>
            <Label>API Key *</Label>
            <Input
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="pk_live_xxxxxxxxxxxxxxxx"
              className="mt-1 font-mono text-sm"
            />
          </div>

          {/* API Secret */}
          <div>
            <Label>API Secret</Label>
            <div className="relative mt-1">
              <Input
                type={showSecret ? 'text' : 'password'}
                value={apiSecret}
                onChange={(e) => setApiSecret(e.target.value)}
                placeholder="sk_live_xxxxxxxxxxxxxxxx"
                className="font-mono text-sm pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full"
                onClick={() => setShowSecret(!showSecret)}
              >
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Opcional - solo para endpoints avanzados</p>
          </div>

          {/* Club ID */}
          <div>
            <Label>Club ID de Playtomic *</Label>
            <Input
              value={clubId}
              onChange={(e) => setClubId(e.target.value)}
              placeholder="ej: 12345 o uuid-del-club"
              className="mt-1 font-mono text-sm"
            />
          </div>

          {/* Test Connection */}
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleTest}
            disabled={testing || !apiKey || !clubId}
            className="w-full"
          >
            {testing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Probando conexión...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Probar Conexión
              </>
            )}
          </Button>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {club?.playtomic_enabled && (
            <Button 
              variant="destructive" 
              onClick={handleDisconnect}
              disabled={saving}
              className="sm:mr-auto"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Desconectar
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Create Local Reservation Modal
const CreateLocalReservationModal = ({ clubs, onClose, onSave }) => {
  const [clubId, setClubId] = useState('');
  const [selectedClub, setSelectedClub] = useState(null);
  const [courtId, setCourtId] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [playerPhone, setPlayerPhone] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:30');
  const [numPlayers, setNumPlayers] = useState(4);
  const [price, setPrice] = useState(600);
  const [saving, setSaving] = useState(false);

  const handleClubChange = (id) => {
    setClubId(id);
    const club = clubs.find(c => c.id === id);
    setSelectedClub(club);
    setCourtId('');
  };

  const handleSave = async () => {
    if (!clubId || !courtId || !playerName || !date || !startTime || !endTime) {
      toast.error('Completa los campos requeridos');
      return;
    }
    setSaving(true);
    try {
      const court = selectedClub?.courts?.find(c => c.id === courtId);
      await onSave({
        club_id: clubId,
        club_name: selectedClub?.name,
        court_id: courtId,
        court_name: court ? `Cancha ${court.court_number}` : courtId,
        player_name: playerName,
        player_phone: playerPhone || null,
        date,
        start_time: startTime,
        end_time: endTime,
        num_players: numPlayers,
        price,
        source: 'local',
        status: 'confirmed',
        payment_status: 'pending'
      });
      toast.success('Reservación local creada');
      onClose();
    } catch (err) {
      toast.error('Error creando reservación');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary" />
            Nueva Reservación Local
          </DialogTitle>
          <DialogDescription>
            Crea una reservación hecha directamente en el club
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Club Selection */}
          <div>
            <Label>Club *</Label>
            <select
              value={clubId}
              onChange={(e) => handleClubChange(e.target.value)}
              className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
              required
            >
              <option value="">Seleccionar club...</option>
              {clubs.map((club) => (
                <option key={club.id} value={club.id}>{club.name}</option>
              ))}
            </select>
          </div>

          {/* Court Selection */}
          {selectedClub && (
            <div>
              <Label>Cancha *</Label>
              <select
                value={courtId}
                onChange={(e) => setCourtId(e.target.value)}
                className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                required
              >
                <option value="">Seleccionar cancha...</option>
                {selectedClub.courts?.map((court) => (
                  <option key={court.id} value={court.id}>Cancha {court.court_number}</option>
                ))}
              </select>
            </div>
          )}

          {/* Player Info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nombre del cliente *</Label>
              <Input
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Juan Pérez"
                className="mt-1"
                required
              />
            </div>
            <div>
              <Label>Teléfono</Label>
              <Input
                value={playerPhone}
                onChange={(e) => setPlayerPhone(e.target.value)}
                placeholder="+52 555 123 4567"
                className="mt-1"
              />
            </div>
          </div>

          {/* Date and Time */}
          <div>
            <Label>Fecha *</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="mt-1"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Hora inicio *</Label>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="mt-1"
                required
              />
            </div>
            <div>
              <Label>Hora fin *</Label>
              <Input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="mt-1"
                required
              />
            </div>
          </div>

          {/* Players and Price */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Jugadores</Label>
              <Input
                type="number"
                value={numPlayers}
                onChange={(e) => setNumPlayers(parseInt(e.target.value) || 4)}
                min={1}
                max={8}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Precio (MXN)</Label>
              <Input
                type="number"
                value={price}
                onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
                className="mt-1"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            Crear Reservación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Component
export default function AdminReservations() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clubs, setClubs] = useState([]);
  const [allReservations, setAllReservations] = useState([]);
  const [globalStats, setGlobalStats] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [activeTab, setActiveTab] = useState('all');
  
  // Modals
  const [apiConfigClub, setApiConfigClub] = useState(null);
  const [showCreateLocal, setShowCreateLocal] = useState(false);
  const [selectedClubDetail, setSelectedClubDetail] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get all clubs
      const clubsRes = await adminAPI.getClubs();
      const allClubs = clubsRes.data.clubs || [];
      
      // Mark which clubs have Playtomic enabled (mock for demo)
      const clubsWithStatus = allClubs.map(club => ({
        ...club,
        playtomic_enabled: club.playtomic_api_key ? true : Math.random() > 0.6,
        last_playtomic_sync: club.last_playtomic_sync || new Date().toISOString()
      }));
      
      setClubs(clubsWithStatus);
      
      // Get reservations (both Playtomic and local)
      let reservations = [];
      
      // Fetch Playtomic reservations
      try {
        const playtomicRes = await playtomicAPI.getReservations({ date: selectedDate });
        const playtomicReservations = (playtomicRes.data.reservations || []).map(r => ({
          ...r,
          source: 'playtomic'
        }));
        reservations = [...reservations, ...playtomicReservations];
      } catch (err) {
        console.log('No Playtomic reservations or API not available');
      }
      
      // Generate mock local reservations for demo
      const localReservations = clubsWithStatus.slice(0, 5).flatMap(club => {
        const count = Math.floor(Math.random() * 4);
        return Array.from({ length: count }, (_, i) => ({
          id: `local-${club.id}-${i}`,
          club_id: club.id,
          club_name: club.name,
          court_name: `Cancha ${(i % 3) + 1}`,
          player_name: ['María García', 'Carlos López', 'Ana Martínez', 'Pedro Sánchez'][i % 4],
          player_phone: '+52 555 ' + Math.floor(Math.random() * 9000000 + 1000000),
          start_time: `${9 + i * 2}:00`,
          end_time: `${10 + i * 2}:30`,
          num_players: 4,
          price: 500 + Math.floor(Math.random() * 300),
          status: ['confirmed', 'paid', 'pending'][i % 3],
          payment_status: ['pending', 'paid_at_club', 'pending'][i % 3],
          source: 'local',
          date: selectedDate
        }));
      });
      
      reservations = [...reservations, ...localReservations];
      setAllReservations(reservations);
      
      // Calculate global stats
      const playtomicCount = reservations.filter(r => r.source === 'playtomic').length;
      const localCount = reservations.filter(r => r.source === 'local').length;
      const totalRevenue = reservations.reduce((sum, r) => sum + (r.price || 0), 0);
      const paidCount = reservations.filter(r => r.status === 'paid' || r.payment_status?.includes('paid')).length;
      const pendingCount = reservations.filter(r => r.payment_status === 'pending').length;
      
      setGlobalStats({
        total_clubs: allClubs.length,
        connected_clubs: clubsWithStatus.filter(c => c.playtomic_enabled).length,
        total_reservations: reservations.length,
        playtomic_reservations: playtomicCount,
        local_reservations: localCount,
        total_revenue: totalRevenue,
        paid: paidCount,
        pending_payment: pendingCount
      });
      
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveApiConfig = async (clubId, config) => {
    // In real implementation, would call backend API
    console.log('Saving API config for club:', clubId, config);
    // Mock save
    await new Promise(resolve => setTimeout(resolve, 500));
    fetchData();
  };

  const handleCreateLocalReservation = async (data) => {
    // In real implementation, would call backend API
    console.log('Creating local reservation:', data);
    // Mock save
    await new Promise(resolve => setTimeout(resolve, 500));
    fetchData();
  };

  // Filter reservations by tab
  const filteredReservations = useMemo(() => {
    let filtered = allReservations;
    
    if (activeTab === 'playtomic') {
      filtered = filtered.filter(r => r.source === 'playtomic');
    } else if (activeTab === 'local') {
      filtered = filtered.filter(r => r.source === 'local');
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        r.player_name?.toLowerCase().includes(query) ||
        r.club_name?.toLowerCase().includes(query) ||
        r.court_name?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [allReservations, activeTab, searchQuery]);

  // Group reservations by club
  const reservationsByClub = useMemo(() => {
    const grouped = {};
    filteredReservations.forEach(r => {
      const clubId = r.club_id;
      if (!grouped[clubId]) {
        grouped[clubId] = [];
      }
      grouped[clubId].push(r);
    });
    return grouped;
  }, [filteredReservations]);

  const changeDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate.toISOString().split('T')[0]);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/admin')}
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Reservaciones
                </h1>
                <p className="text-sm text-muted-foreground">
                  Playtomic y reservas locales de todos los clubs
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline"
                onClick={() => setShowCreateLocal(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Nueva Local
              </Button>
              <Button onClick={fetchData} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Date Navigation */}
        <div className="flex items-center justify-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => changeDate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="text-center">
            <p className="font-display font-bold text-lg capitalize">{formatDate(selectedDate)}</p>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="text-xs text-muted-foreground bg-transparent border-none cursor-pointer text-center"
            />
          </div>
          <Button variant="ghost" size="icon" onClick={() => changeDate(1)}>
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Global Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          <div className="bg-card rounded-xl p-3 border border-border text-center">
            <Building2 className="w-5 h-5 mx-auto mb-1 text-primary" />
            <p className="text-xl font-bold">{globalStats.total_clubs || 0}</p>
            <p className="text-xs text-muted-foreground">Clubs</p>
          </div>
          <div className="bg-green-500/10 rounded-xl p-3 text-center">
            <Wifi className="w-5 h-5 mx-auto mb-1 text-green-500" />
            <p className="text-xl font-bold text-green-500">{globalStats.connected_clubs || 0}</p>
            <p className="text-xs text-muted-foreground">Con API</p>
          </div>
          <div className="bg-purple-500/10 rounded-xl p-3 text-center">
            <Globe className="w-5 h-5 mx-auto mb-1 text-purple-500" />
            <p className="text-xl font-bold text-purple-500">{globalStats.playtomic_reservations || 0}</p>
            <p className="text-xs text-muted-foreground">Playtomic</p>
          </div>
          <div className="bg-blue-500/10 rounded-xl p-3 text-center">
            <Home className="w-5 h-5 mx-auto mb-1 text-blue-500" />
            <p className="text-xl font-bold text-blue-500">{globalStats.local_reservations || 0}</p>
            <p className="text-xs text-muted-foreground">Locales</p>
          </div>
          <div className="bg-card rounded-xl p-3 border border-border text-center">
            <Calendar className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
            <p className="text-xl font-bold">{globalStats.total_reservations || 0}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </div>
          <div className="bg-primary/10 rounded-xl p-3 text-center">
            <DollarSign className="w-5 h-5 mx-auto mb-1 text-primary" />
            <p className="text-xl font-bold">${(globalStats.total_revenue || 0).toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">Ingresos</p>
          </div>
          <div className="bg-green-500/10 rounded-xl p-3 text-center">
            <CheckCircle className="w-5 h-5 mx-auto mb-1 text-green-500" />
            <p className="text-xl font-bold text-green-500">{globalStats.paid || 0}</p>
            <p className="text-xs text-muted-foreground">Pagados</p>
          </div>
          <div className="bg-yellow-500/10 rounded-xl p-3 text-center">
            <AlertCircle className="w-5 h-5 mx-auto mb-1 text-yellow-500" />
            <p className="text-xl font-bold text-yellow-500">{globalStats.pending_payment || 0}</p>
            <p className="text-xs text-muted-foreground">Por cobrar</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <TabsList>
              <TabsTrigger value="all" className="gap-2">
                <Calendar className="w-4 h-4" />
                Todas
              </TabsTrigger>
              <TabsTrigger value="playtomic" className="gap-2">
                <Globe className="w-4 h-4" />
                Playtomic
              </TabsTrigger>
              <TabsTrigger value="local" className="gap-2">
                <Home className="w-4 h-4" />
                Locales
              </TabsTrigger>
              <TabsTrigger value="api-config" className="gap-2">
                <Key className="w-4 h-4" />
                Config API
              </TabsTrigger>
            </TabsList>
            
            {activeTab !== 'api-config' && (
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar cliente, club, cancha..."
                  className="pl-9"
                />
              </div>
            )}
          </div>

          {/* All / Playtomic / Local Tabs Content */}
          <TabsContent value="all" className="mt-6">
            <ReservationsListView 
              clubs={clubs}
              reservationsByClub={reservationsByClub}
              loading={loading}
              onViewDetails={setSelectedClubDetail}
              onConfigureApi={setApiConfigClub}
            />
          </TabsContent>
          
          <TabsContent value="playtomic" className="mt-6">
            <ReservationsListView 
              clubs={clubs.filter(c => c.playtomic_enabled)}
              reservationsByClub={reservationsByClub}
              loading={loading}
              onViewDetails={setSelectedClubDetail}
              onConfigureApi={setApiConfigClub}
              emptyMessage="No hay reservaciones de Playtomic para este día"
            />
          </TabsContent>
          
          <TabsContent value="local" className="mt-6">
            <ReservationsListView 
              clubs={clubs}
              reservationsByClub={reservationsByClub}
              loading={loading}
              onViewDetails={setSelectedClubDetail}
              onConfigureApi={setApiConfigClub}
              emptyMessage="No hay reservaciones locales para este día"
            />
          </TabsContent>
          
          {/* API Config Tab */}
          <TabsContent value="api-config" className="mt-6">
            <ApiConfigView 
              clubs={clubs}
              onConfigure={setApiConfigClub}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Modals */}
      {apiConfigClub && (
        <ApiConfigModal
          club={apiConfigClub}
          onClose={() => setApiConfigClub(null)}
          onSave={(config) => handleSaveApiConfig(apiConfigClub.id, config)}
        />
      )}
      
      {showCreateLocal && (
        <CreateLocalReservationModal
          clubs={clubs}
          onClose={() => setShowCreateLocal(false)}
          onSave={handleCreateLocalReservation}
        />
      )}
    </div>
  );
}

// Reservations List View Component
function ReservationsListView({ clubs, reservationsByClub, loading, onViewDetails, onConfigureApi, emptyMessage }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="ml-3 text-muted-foreground">Cargando reservaciones...</p>
      </div>
    );
  }

  const clubsWithReservations = clubs.filter(c => (reservationsByClub[c.id]?.length || 0) > 0);
  
  if (clubsWithReservations.length === 0) {
    return (
      <div className="text-center py-12">
        <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium">Sin reservaciones</h3>
        <p className="text-muted-foreground">{emptyMessage || 'No hay reservaciones para este día'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {clubsWithReservations.map((club) => (
        <ClubReservationsCard
          key={club.id}
          club={club}
          reservations={reservationsByClub[club.id] || []}
          onViewDetails={onViewDetails}
          onConfigureApi={onConfigureApi}
        />
      ))}
    </div>
  );
}

// API Config View Component
function ApiConfigView({ clubs, onConfigure }) {
  const connectedClubs = clubs.filter(c => c.playtomic_enabled);
  const disconnectedClubs = clubs.filter(c => !c.playtomic_enabled);

  return (
    <div className="space-y-6">
      {/* Info */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Key className="w-5 h-5 text-blue-400 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-400">Configuración de API Playtomic</h3>
            <p className="text-sm text-muted-foreground">
              Cada club puede conectar su propia cuenta de Playtomic para sincronizar reservaciones 
              automáticamente. Las credenciales son privadas de cada club.
            </p>
          </div>
        </div>
      </div>

      {/* Connected Clubs */}
      <div>
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Wifi className="w-4 h-4 text-green-400" />
          Clubs con API Conectada ({connectedClubs.length})
        </h3>
        {connectedClubs.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-3">
            {connectedClubs.map((club) => (
              <div 
                key={club.id}
                className="bg-card rounded-xl border border-green-500/30 p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/20">
                    <Building2 className="w-4 h-4 text-green-400" />
                  </div>
                  <div>
                    <p className="font-medium">{club.name}</p>
                    <p className="text-xs text-muted-foreground">{club.city}</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => onConfigure(club)}>
                  <Settings className="w-4 h-4 mr-1" />
                  Configurar
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Ningún club tiene API configurada</p>
        )}
      </div>

      {/* Disconnected Clubs */}
      <div>
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <WifiOff className="w-4 h-4 text-gray-400" />
          Clubs sin API ({disconnectedClubs.length})
        </h3>
        {disconnectedClubs.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {disconnectedClubs.map((club) => (
              <div 
                key={club.id}
                className="bg-card rounded-xl border border-border p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-secondary">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-medium">{club.name}</p>
                    <p className="text-xs text-muted-foreground">{club.city}</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => onConfigure(club)}>
                  <Key className="w-4 h-4 mr-1" />
                  Conectar
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Todos los clubs tienen API configurada</p>
        )}
      </div>
    </div>
  );
}
